import { useCallback, useEffect, useRef, useState } from 'react';
import { TICKER_TIMING } from '@/extensions/ticker/lib/constants';

type UseTickerMarqueeOptions = {
    isActive: boolean;
    isPaused: boolean;
    /** When true, the scroll cycle repeats on the same slide (single-slide ticker). */
    loop: boolean;
    /** Delay before starting the marquee after the slide becomes active. */
    startDelayMs?: number;
    onCycleComplete?: () => void;
};

type UseTickerMarqueeResult = {
    viewportRef: React.RefObject<HTMLDivElement | null>;
    contentRef: React.RefObject<HTMLDivElement | null>;
    translateX: number;
    contentOverflows: boolean;
    shouldAnimate: boolean;
};

function getScrollEndOffsetPx(): number {
    if (typeof window === 'undefined') {
        return 10;
    }

    const raw = getComputedStyle(document.documentElement).getPropertyValue('--ticker-scroll-end-offset').trim();
    if (!raw) {
        return 10;
    }

    if (raw.endsWith('rem')) {
        // eslint-disable-next-line unicorn/prefer-number-coercion
        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        // eslint-disable-next-line unicorn/prefer-number-coercion
        return Number.parseFloat(raw) * rootFontSize;
    }

    // eslint-disable-next-line unicorn/prefer-number-coercion
    return Number.parseFloat(raw) || 10;
}

function prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useTickerMarquee({
    isActive,
    isPaused,
    loop,
    startDelayMs = 0,
    onCycleComplete,
}: UseTickerMarqueeOptions): UseTickerMarqueeResult {
    const viewportRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [translateX, setTranslateX] = useState(0);
    const [contentOverflows, setContentOverflows] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(() => !prefersReducedMotion());
    const scrollDistanceRef = useRef(0);
    const lastOverflowRef = useRef(-1);

    useEffect(() => {
        const mediaQuery = matchMedia('(prefers-reduced-motion: reduce)');
        const syncMotionPreference = () => {
            setShouldAnimate(!mediaQuery.matches);
        };

        syncMotionPreference();
        mediaQuery.addEventListener('change', syncMotionPreference);

        return () => {
            mediaQuery.removeEventListener('change', syncMotionPreference);
        };
    }, []);

    const measureOverflow = useCallback(() => {
        const viewport = viewportRef.current;
        const content = contentRef.current;
        if (!viewport || !content) {
            return 0;
        }

        const { paddingLeft, paddingRight } = getComputedStyle(viewport);
        // eslint-disable-next-line unicorn/prefer-number-coercion
        const paddingInline = (Number.parseFloat(paddingLeft) || 0) + (Number.parseFloat(paddingRight) || 0);
        const contentAreaWidth = viewport.clientWidth - paddingInline;

        return Math.max(0, content.scrollWidth - contentAreaWidth);
    }, []);

    const syncContentOverflows = useCallback(() => {
        setContentOverflows(measureOverflow() > 0);
    }, [measureOverflow]);

    useEffect(() => {
        syncContentOverflows();

        const observer = new ResizeObserver(() => {
            syncContentOverflows();
        });
        const viewport = viewportRef.current;
        const content = contentRef.current;
        if (viewport) {
            observer.observe(viewport);
        }
        if (content) {
            observer.observe(content);
        }

        return () => observer.disconnect();
    }, [syncContentOverflows]);

    useEffect(() => {
        if (!isActive || isPaused) {
            setTranslateX(0);
            return;
        }

        let cancelled = false;
        const timers: ReturnType<typeof setTimeout>[] = [];

        const schedule = (callback: () => void, delayMs: number) => {
            timers.push(
                setTimeout(() => {
                    if (!cancelled) {
                        callback();
                    }
                }, delayMs)
            );
        };

        const startCycle = () => {
            if (cancelled) {
                return;
            }

            setTranslateX(0);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (cancelled) {
                        return;
                    }

                    const overflow = measureOverflow();
                    lastOverflowRef.current = overflow;
                    setContentOverflows(overflow > 0);
                    const scrollEndOffset = getScrollEndOffsetPx();
                    scrollDistanceRef.current = overflow > 0 ? overflow + scrollEndOffset : 0;

                    if (!shouldAnimate) {
                        if (loop) {
                            schedule(startCycle, TICKER_TIMING.autoSlideMs);
                        } else {
                            schedule(() => {
                                onCycleComplete?.();
                            }, TICKER_TIMING.autoSlideMs);
                        }
                        return;
                    }

                    if (overflow > 0) {
                        schedule(() => {
                            setTranslateX(-scrollDistanceRef.current);
                        }, TICKER_TIMING.scrollDelayMs);

                        schedule(() => {
                            if (loop) {
                                setTranslateX(0);
                                schedule(
                                    startCycle,
                                    TICKER_TIMING.singleSlideResetMs - TICKER_TIMING.pauseAfterScrollMs
                                );
                            } else {
                                onCycleComplete?.();
                            }
                        }, TICKER_TIMING.pauseAfterScrollMs);
                    } else if (loop) {
                        schedule(startCycle, TICKER_TIMING.autoSlideMs);
                    } else {
                        schedule(() => {
                            onCycleComplete?.();
                        }, TICKER_TIMING.autoSlideMs);
                    }
                });
            });
        };

        const observer = new ResizeObserver(() => {
            if (cancelled || !isActive || isPaused) {
                return;
            }

            const overflow = measureOverflow();
            if (overflow === lastOverflowRef.current) {
                return;
            }

            lastOverflowRef.current = overflow;
            cancelled = true;
            for (const timer of timers) {
                clearTimeout(timer);
            }
            cancelled = false;
            startCycle();
        });

        const viewport = viewportRef.current;
        const content = contentRef.current;
        if (viewport) {
            observer.observe(viewport);
        }
        if (content) {
            observer.observe(content);
        }

        schedule(startCycle, startDelayMs);

        return () => {
            cancelled = true;
            for (const timer of timers) {
                clearTimeout(timer);
            }
            observer.disconnect();
        };
    }, [isActive, isPaused, loop, onCycleComplete, measureOverflow, startDelayMs, shouldAnimate]);

    return {
        viewportRef,
        contentRef,
        translateX,
        contentOverflows,
        shouldAnimate,
    };
}
